import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth, API } from "../App";
import AppShell from "./AppShell";
import axios from "axios";
import { Check, Loader2, Crown, Zap, Star } from "lucide-react";

const Billing = () => {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [billingPlan, setBillingPlan] = useState("monthly");
  const [loading, setLoading] = useState(false);
  const [ltdLoading, setLtdLoading] = useState(false);
  const [billingStatus, setBillingStatus] = useState(null);
  const [planInfo, setPlanInfo] = useState(null);
  const [ltdStatus, setLtdStatus] = useState(null);
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    fetchBillingData();
  }, []);

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    const success = searchParams.get("success");
    const isLtd = searchParams.get("ltd");
    if (sessionId && success === "true") {
      if (isLtd === "true") {
        verifyLtdSession(sessionId);
      } else {
        verifySession(sessionId);
      }
    }
  }, [searchParams]);

  const fetchBillingData = async () => {
    try {
      const [statusRes, planRes, ltdRes] = await Promise.all([
        axios.get(`${API}/billing/status`),
        axios.get(`${API}/user/plan`),
        axios.get(`${API}/ltd/status`)
      ]);
      setBillingStatus(statusRes.data);
      setPlanInfo(planRes.data);
      setLtdStatus(ltdRes.data);
    } catch (err) {
      console.error("Failed to fetch billing data:", err);
    }
  };

  const verifySession = async (sessionId) => {
    setVerifying(true);
    try {
      const { data } = await axios.post(`${API}/billing/verify-session`, { session_id: sessionId });
      if (data.plan === "pro" || data.plan === "lifetime") {
        setUser(prev => ({ ...prev, plan: data.plan }));
        await fetchBillingData();
      }
    } catch (err) {
      console.error("Failed to verify session:", err);
    } finally {
      setVerifying(false);
    }
  };

  const verifyLtdSession = async (sessionId) => {
    setVerifying(true);
    try {
      const { data } = await axios.post(`${API}/ltd/verify-session`, { session_id: sessionId });
      if (data.plan === "lifetime") {
        setUser(prev => ({ ...prev, plan: "lifetime" }));
        await fetchBillingData();
      }
    } catch (err) {
      console.error("Failed to verify LTD session:", err);
    } finally {
      setVerifying(false);
    }
  };

  const handleUpgrade = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/billing/checkout`, {
        plan: billingPlan,
        origin_url: window.location.origin
      });
      if (data.url) window.location.href = data.url;
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to start checkout");
    } finally {
      setLoading(false);
    }
  };

  const handleLtdPurchase = async () => {
    setLtdLoading(true);
    try {
      const { data } = await axios.post(`${API}/ltd/checkout`, {
        origin_url: window.location.origin
      });
      if (data.url) window.location.href = data.url;
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to start checkout");
    } finally {
      setLtdLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm("Cancel your subscription? You'll keep Pro until the end of your billing period.")) return;
    try {
      await axios.post(`${API}/billing/cancel`);
      await fetchBillingData();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to cancel");
    }
  };

  const currentPlan = billingStatus?.plan || planInfo?.plan || "free";
  const isLifetime = currentPlan === "lifetime";
  const isPro = currentPlan === "pro";
  const isFree = !isPro && !isLifetime;

  return (
    <AppShell active="billing">
      {verifying && (
        <div className="bg-[#FEF3C7] border-b border-[#F59E0B] px-6 py-3 flex items-center justify-center gap-2 -mx-4 sm:-mx-6 lg:-mx-8 -mt-4 sm:-mt-6 lg:-mt-8 mb-6">
          <Loader2 className="w-4 h-4 animate-spin text-[#D97706]" />
          <span className="text-[14px] font-medium text-[#92400E]">Verifying your payment...</span>
        </div>
      )}

      <div className="mb-6">
        <h1 className="font-display text-[22px] sm:text-[28px] tracking-[-0.03em] text-[#0A0A0A]">Billing</h1>
        <p className="text-[13px] sm:text-[14px] text-[#6B7280] mt-1">Manage your plan and subscription</p>
      </div>

      <div className="max-w-[700px]">
        {/* Current Plan Status */}
        <div className="bg-white border border-[#E5E7EB] p-4 sm:p-6 mb-6" data-testid="current-plan-section">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 flex items-center justify-center ${isLifetime ? 'bg-[#F59E0B]' : isPro ? 'bg-[#1A3DFF]' : 'bg-[#E5E7EB]'}`}>
                {isLifetime ? <Star className="w-5 h-5 text-white" /> : isPro ? <Crown className="w-5 h-5 text-white" /> : <Zap className="w-5 h-5 text-[#6B7280]" />}
              </div>
              <div>
                <p className="font-semibold text-[16px] text-[#0A0A0A]">
                  {isLifetime ? "Lifetime Plan" : isPro ? "Pro Plan" : "Free Plan"}
                </p>
                <p className="text-[13px] text-[#6B7280]">
                  {isLifetime
                    ? "Unlimited everything, forever. No recurring fees."
                    : isPro
                    ? "Unlimited invoices, all features"
                    : `${planInfo?.invoices_this_month || 0} of ${planInfo?.invoice_limit || 10} invoices used this month`}
                </p>
              </div>
            </div>
            {isLifetime && (
              <span className="text-[11px] font-bold text-[#F59E0B] bg-[#FEF3C7] px-2.5 py-1 uppercase tracking-[0.05em]" data-testid="lifetime-badge">
                Lifetime
              </span>
            )}
            {isPro && billingStatus?.cancel_at_period_end && (
              <span className="text-[12px] font-medium text-[#F59E0B] bg-[#FEF3C7] px-2 py-1">Cancels at period end</span>
            )}
          </div>

          {isFree && planInfo && (
            <div className="w-full bg-[#E5E7EB] h-2 mt-3">
              <div
                className="h-2 transition-all"
                style={{
                  width: `${Math.min(100, (planInfo.invoices_this_month / planInfo.invoice_limit) * 100)}%`,
                  backgroundColor: (planInfo.invoices_remaining || 0) <= 2 ? '#EF4444' : '#1A3DFF'
                }}
              />
            </div>
          )}
        </div>

        {/* LTD Offer - Only for free users when spots available */}
        {isFree && ltdStatus?.is_available && (
          <div className="relative bg-[#0A0A0A] p-5 sm:p-8 mb-6 overflow-hidden" data-testid="ltd-offer-section">
            {/* Accent corner */}
            <div className="absolute top-0 right-0 w-24 h-24 bg-[#F59E0B] transform translate-x-12 -translate-y-12 rotate-45" />
            <div className="relative">
              <div className="flex items-center gap-2 mb-1">
                <Star className="w-4 h-4 text-[#F59E0B]" />
                <span className="text-[11px] font-bold text-[#F59E0B] tracking-[0.08em] uppercase">Limited Offer</span>
              </div>
              <h2 className="font-display text-[24px] sm:text-[28px] tracking-[-0.02em] text-white mb-2">
                Lifetime deal — $99
              </h2>
              <p className="text-[14px] text-white/50 mb-1">
                Pay once, use forever. Everything in Pro, no monthly fees.
              </p>
              <p className="text-[14px] text-white/70 font-medium mb-6">
                <span className="text-[#F59E0B] font-bold">{ltdStatus.remaining}</span> of {ltdStatus.total_spots} spots remaining
              </p>

              {/* Spots progress bar */}
              <div className="w-full bg-white/10 h-2 mb-6">
                <div
                  className="h-2 bg-[#F59E0B] transition-all"
                  style={{ width: `${(ltdStatus.claimed / ltdStatus.total_spots) * 100}%` }}
                />
              </div>

              <button
                onClick={handleLtdPurchase}
                disabled={ltdLoading}
                className="w-full py-3.5 bg-[#F59E0B] text-[#0A0A0A] font-bold text-[15px] hover:bg-[#D97706] transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="ltd-purchase-btn"
              >
                {ltdLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Star className="w-4 h-4" />}
                {ltdLoading ? "Redirecting to checkout..." : "Get lifetime access — $99"}
              </button>

              {/* Value comparison */}
              <div className="mt-5 pt-5 border-t border-white/10">
                <p className="text-[12px] text-white/40 mb-3 uppercase tracking-wide font-semibold">Compare the value</p>
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div className="bg-white/5 p-3">
                    <p className="text-[12px] text-white/40">Monthly</p>
                    <p className="font-display text-[18px] text-white/60 line-through">$108<span className="text-[11px]">/yr</span></p>
                  </div>
                  <div className="bg-white/5 p-3">
                    <p className="text-[12px] text-white/40">Annual</p>
                    <p className="font-display text-[18px] text-white/60 line-through">$79<span className="text-[11px]">/yr</span></p>
                  </div>
                  <div className="bg-[#F59E0B]/10 border border-[#F59E0B]/30 p-3">
                    <p className="text-[12px] text-[#F59E0B]">Lifetime</p>
                    <p className="font-display text-[18px] text-[#F59E0B] font-bold">$99<span className="text-[11px]"> once</span></p>
                  </div>
                </div>
              </div>

              {/* Features */}
              <div className="grid grid-cols-2 gap-2.5 mt-6">
                {[
                  "Unlimited invoices",
                  "Recurring invoices",
                  "3 PDF templates",
                  "Email from your domain",
                  "Remove footer branding",
                  "All future updates",
                  "Priority support",
                  "No recurring fees"
                ].map((f, i) => (
                  <div key={i} className="flex items-center gap-2 text-[13px] text-white/70">
                    <Check className="w-3.5 h-3.5 text-[#F59E0B] flex-shrink-0" />
                    {f}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Regular Subscription Upgrade - for free users */}
        {isFree && (
          <div className="bg-white border border-[#E5E7EB] p-5 sm:p-8" data-testid="upgrade-section">
            <div className="flex items-center gap-2 mb-2">
              <Crown className="w-4 h-4 text-[#1A3DFF]" />
              <p className="text-[11px] font-semibold text-[#6B7280] tracking-[0.08em] uppercase">Or subscribe to Pro</p>
            </div>
            <p className="text-[14px] text-[#6B7280] mb-6">Prefer a subscription? Cancel anytime.</p>

            <div className="inline-flex border border-[#E5E7EB] mb-6">
              <button
                onClick={() => setBillingPlan("monthly")}
                className={`px-5 py-2.5 text-[13px] font-semibold transition-colors ${billingPlan === "monthly" ? "bg-[#1A3DFF] text-white" : "text-[#6B7280] hover:text-[#0A0A0A]"}`}
                data-testid="monthly-plan-toggle"
              >
                Monthly — $9/mo
              </button>
              <button
                onClick={() => setBillingPlan("annual")}
                className={`px-5 py-2.5 text-[13px] font-semibold transition-colors ${billingPlan === "annual" ? "bg-[#1A3DFF] text-white" : "text-[#6B7280] hover:text-[#0A0A0A]"}`}
                data-testid="annual-plan-toggle"
              >
                Annual — $79/yr
                <span className="ml-1.5 text-[11px] text-[#10B981] font-bold">SAVE 27%</span>
              </button>
            </div>

            <button
              onClick={handleUpgrade}
              disabled={loading}
              className="w-full py-3 bg-[#0A0A0A] text-white font-bold text-[14px] hover:bg-[#1A3DFF] transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="upgrade-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {loading ? "Redirecting..." : `Subscribe — $${billingPlan === "monthly" ? "9/mo" : "79/yr"}`}
            </button>
          </div>
        )}

        {/* Pro user management */}
        {isPro && (
          <div className="bg-white border border-[#E5E7EB] p-4 sm:p-6" data-testid="manage-subscription">
            <h3 className="font-semibold text-[16px] text-[#0A0A0A] mb-4">Manage Subscription</h3>
            {billingStatus?.current_period_end && (
              <p className="text-[14px] text-[#6B7280] mb-4">
                Next billing date: {new Date(billingStatus.current_period_end * 1000).toLocaleDateString()}
              </p>
            )}
            {!billingStatus?.cancel_at_period_end && (
              <button
                onClick={handleCancel}
                className="px-4 py-2 text-[13px] font-medium text-[#EF4444] border border-[#FECACA] hover:bg-[#FEF2F2] transition-colors"
                data-testid="cancel-subscription-btn"
              >
                Cancel subscription
              </button>
            )}
          </div>
        )}

        {/* Lifetime user - no management needed */}
        {isLifetime && (
          <div className="bg-white border border-[#E5E7EB] p-4 sm:p-6" data-testid="lifetime-details">
            <h3 className="font-semibold text-[16px] text-[#0A0A0A] mb-2">Your Lifetime Access</h3>
            <p className="text-[14px] text-[#6B7280]">
              You have permanent access to all Pro features. No recurring charges, no expiry. All future updates included.
            </p>
          </div>
        )}
      </div>
    </AppShell>
  );
};

export default Billing;
